/**
 * Semantic search tools — finding footage by MEANING, and by its relations in
 * the embedding space (docs/agent.md §9).
 *
 * Backed by the pandora-embeddings service, which has embedded the AI shot
 * descriptions of a set of indiancine.ma films. Four tools:
 *
 *   semantic_search   describe a shot; ranked matches, with a confidence band
 *   similar_shots     shots that look like an anchor shot, anywhere in the corpus
 *   steer_shot        the anchor nudged towards a phrase — "this, but at night"
 *   trajectory        an ARC through waypoints, real shots along the way, in order
 *
 * They live in their own registry (`EMBEDDING_TOOLS`) rather than `READ_TOOLS`:
 * the composer's "Semantic search" toggle decides whether a run gets them at
 * all, and with it off the agent is exactly what it was before.
 *
 * Two conventions carried over from search_moments (tools/search.ts): every
 * hit is shown to the user as a clip card via `ctx.postChat`, and the model gets
 * a digest. The digest here always carries the annotation id, because that id
 * is the anchor the relational tools take — it is how "find more like the third
 * one" becomes a call.
 *
 * `rerank` is never sent. The cross-encoder takes tens of seconds on the
 * service's CPU; a tool loop with a 180 s wall clock cannot afford it.
 */
import type { ClipCard, JsonSchemaProp, ToolResult } from '../core/types';
import { toolError } from '../core/types';
import type { ReadContext, ReadToolDef } from './catalog';
import type {
  Anchor,
  ChainEntry,
  EmbeddingHit,
  EmbeddingsPort,
  RegionFilters,
  RegionHit,
  SceneHit,
  ShotFilters,
  ShotSearchBody,
  TrajectoryWaypoint,
} from './embeddingsTypes';
import { round, truncate } from './search';

/** Per-card blurb, as for search_moments. The full description is in the digest. */
const CARD_NOTE_CHARS = 90;
/** Rows the model sees from a search, in rank order — relevance is the point here. */
const SAMPLE_ROWS = 10;
/** Rows the model sees from similar/steer. */
const NEIGHBOUR_ROWS = 12;
const DEFAULT_LIMIT = 30;
const MAX_LIMIT = 200;
/** add_sequence takes at most this many clips, so a chain longer than it cannot be laid down in one call. */
const MAX_CHAIN = 20;
const DEFAULT_STEPS = 3;

type Kind = 'shots' | 'scenes' | 'objects';

// ---------------------------------------------------------------------------
// Schema fragments
// ---------------------------------------------------------------------------

const RANGE: JsonSchemaProp = {
  type: 'object',
  properties: { from: { type: 'number' }, to: { type: 'number' } },
};

const SHOT_FILTER_PROPS: Record<string, JsonSchemaProp> = {
  item: { type: 'string', description: 'only this item id, or several joined with +' },
  year: { ...RANGE, description: 'film year, e.g. {"from": 1950, "to": 1970}' },
  director: { type: 'string', description: 'part of a director\'s name, e.g. "Mrinal Sen"' },
  language: { type: 'string', description: 'film language, e.g. "Bengali"' },
  min_duration: {
    type: 'number',
    minimum: 0,
    description: 'shot length in seconds; 2 or more gives cuttable shots',
  },
  max_duration: { type: 'number', minimum: 0 },
  camera_motion: {
    type: 'array',
    items: {
      type: 'string',
      enum: ['static', 'pan', 'tilt', 'zoom', 'tracking', 'handheld', 'cut'],
    },
    description: 'shots only',
  },
  has_ocr: { type: 'boolean', description: 'shots with burned-in text (titles, subtitles); shots only' },
  is_monochrome: { type: 'boolean', description: 'black-and-white shots; shots only' },
  max_rightslevel: {
    type: 'integer',
    minimum: 0,
    description: '1 = films guests may play; omit to search everything',
  },
};

const OBJECT_FILTER_PROPS: Record<string, JsonSchemaProp> = {
  min_area: {
    type: 'number',
    minimum: 0,
    maximum: 1,
    description: 'objects only: fraction of the frame the box covers; 0.3+ reads as a close-up',
  },
  max_area: { type: 'number', minimum: 0, maximum: 1, description: 'objects only' },
  cx: { ...RANGE, description: 'objects only: box centre x, 0 left … 1 right' },
  cy: { ...RANGE, description: 'objects only: box centre y, 0 top … 1 bottom' },
};

const SHOT_FILTERS: JsonSchemaProp = {
  type: 'object',
  description: 'narrow the shots, e.g. {"min_duration": 2, "max_duration": 12}',
  properties: SHOT_FILTER_PROPS,
};

const ALL_FILTERS: JsonSchemaProp = {
  type: 'object',
  description:
    'narrow the results, e.g. {"min_duration": 2, "max_duration": 12}; area/cx/cy only with kind "objects"',
  properties: { ...SHOT_FILTER_PROPS, ...OBJECT_FILTER_PROPS },
};

const ANCHOR: JsonSchemaProp = {
  type: 'object',
  description:
    '{"id": "OJV/ABC"} — an annotation id from an earlier semantic result — or {"item": "OJV", "at": 5173.7}: an item and a concat time inside it, e.g. a timeline clip\'s in-point',
  properties: {
    id: { type: 'string' },
    item: { type: 'string' },
    at: { type: 'number', minimum: 0 },
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const NOT_ENABLED =
  'semantic search is not enabled for this run — the user can turn on the "Semantic search" toggle in the chat composer. Use search_moments instead.';

function port(ctx: ReadContext): EmbeddingsPort | null {
  return ctx.embeddings ?? null;
}

function failed(what: string, err: unknown): ToolResult {
  const msg = err instanceof Error ? err.message : String(err);
  return toolError(
    `${what} failed: ${msg}. If the semantic search service keeps failing, use search_moments instead.`,
  );
}

function clampInt(raw: unknown, fallback: number, min: number, max: number): number {
  const n = typeof raw === 'number' && Number.isFinite(raw) ? Math.floor(raw) : fallback;
  return Math.min(Math.max(n, min), max);
}

/**
 * Turn the model's flat `filters` into the service's schema. The flat shape is
 * deliberate — cheap models handle `{"year": {"from": 1950}}` far better than
 * `year_min` — and every key is checked here so the error can say what is valid.
 */
/** Shot facets the object index does not carry. */
const SHOTS_ONLY = new Set(['director', 'min_duration', 'max_duration', 'camera_motion', 'has_ocr', 'is_monochrome']);

function toFilters(
  raw: unknown,
  kind: Kind,
): { ok: true; filters: ShotFilters & RegionFilters } | { ok: false; error: string } {
  const out: ShotFilters & RegionFilters = {};
  if (!raw || typeof raw !== 'object') return { ok: true, filters: out };
  const f = raw as Record<string, unknown>;
  const range = (v: unknown): { from?: number; to?: number } =>
    v && typeof v === 'object' ? (v as { from?: number; to?: number }) : {};

  for (const key of Object.keys(f)) {
    if (f[key] === undefined || f[key] === null) continue;
    const objectOnly = key in OBJECT_FILTER_PROPS;
    if (objectOnly && kind !== 'objects') {
      return {
        ok: false,
        error: `filter "${key}" applies only to kind "objects" (detected things with a position). For shots and scenes use: ${Object.keys(SHOT_FILTER_PROPS).join(', ')}`,
      };
    }
    if (SHOTS_ONLY.has(key) && kind === 'objects') {
      return {
        ok: false,
        error: `filter "${key}" does not apply to kind "objects" — objects take item, year, language, max_rightslevel, area, cx and cy`,
      };
    }
  }

  if (typeof f.item === 'string' && f.item.trim()) {
    out.item_ids = f.item
      .split('+')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (f.year) {
    const r = range(f.year);
    if (typeof r.from === 'number') out.year_min = Math.floor(r.from);
    if (typeof r.to === 'number') out.year_max = Math.floor(r.to);
  }
  if (typeof f.director === 'string' && f.director.trim()) out.director = f.director.trim();
  if (typeof f.language === 'string' && f.language.trim()) out.language = f.language.trim();
  if (typeof f.has_ocr === 'boolean') out.has_ocr = f.has_ocr;
  if (typeof f.is_monochrome === 'boolean') out.is_monochrome = f.is_monochrome;
  if (typeof f.min_duration === 'number') out.min_duration = f.min_duration;
  if (typeof f.max_duration === 'number') out.max_duration = f.max_duration;
  if (Array.isArray(f.camera_motion) && f.camera_motion.length) {
    out.camera_motion = f.camera_motion.map(String);
  }
  if (typeof f.max_rightslevel === 'number') out.max_rightslevel = Math.floor(f.max_rightslevel);
  if (typeof f.min_area === 'number') out.min_area = f.min_area;
  if (typeof f.max_area === 'number') out.max_area = f.max_area;
  if (f.cx) {
    const r = range(f.cx);
    if (typeof r.from === 'number') out.cx_min = r.from;
    if (typeof r.to === 'number') out.cx_max = r.to;
  }
  if (f.cy) {
    const r = range(f.cy);
    if (typeof r.from === 'number') out.cy_min = r.from;
    if (typeof r.to === 'number') out.cy_max = r.to;
  }
  return { ok: true, filters: out };
}

function parseAnchor(raw: unknown): { ok: true; anchor: Anchor } | { ok: false; error: string } {
  const a = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  if (typeof a.id === 'string' && a.id.trim()) return { ok: true, anchor: { id: a.id.trim() } };
  if (typeof a.item === 'string' && a.item.trim() && typeof a.at === 'number') {
    return { ok: true, anchor: { item: a.item.trim(), t: a.at } };
  }
  return {
    ok: false,
    error:
      'anchor must be {"id": "ITEM/ANN"} (an annotation id from a semantic result) or {"item": "ITEM", "at": seconds} (a concat time inside an item, e.g. a timeline clip\'s in-point)',
  };
}

function anchorLabel(anchor: Anchor): string {
  return 'id' in anchor ? anchor.id : `${anchor.item} @ ${anchor.t}s`;
}

/** Card note: the prose is what was embedded, so it says why the shot matched. */
function noteOf(h: { prose?: string; text?: string }, max = CARD_NOTE_CHARS): string {
  return truncate(h.prose || h.text || '', max);
}

function shotCard(h: EmbeddingHit, archive: string): ClipCard {
  return {
    archive,
    item: h.item.id,
    title: h.item.title || h.item.id,
    in: round(h.t_in),
    out: round(h.t_out),
    note: noteOf(h),
  };
}

function round2(n: number | null | undefined): number | undefined {
  return typeof n === 'number' ? Math.round(n * 100) / 100 : undefined;
}

/** One digest row: everything the model needs to add the clip or anchor on it. */
function shotRow(h: EmbeddingHit, textChars: number) {
  return {
    id: h.id,
    item: h.item.id,
    in: round(h.t_in),
    out: round(h.t_out),
    title: h.item.title,
    ...(typeof h.score === 'number' ? { score: round2(h.score) } : {}),
    ...(textChars > 0 ? { text: noteOf(h, textChars) } : {}),
  };
}

/**
 * Build the digest at the largest text size that fits the tool's budget. The
 * loop clips oversize results by slicing the JSON, which would cut a chain
 * mid-entry and lose the tail of an arc; shrinking the text per row first keeps
 * every in/out visible.
 */
function fitDigest(
  build: (textChars: number) => { ok: true; [key: string]: unknown },
  budget: number,
): ToolResult {
  for (const chars of [200, 140, 100, 70, 40, 0]) {
    const digest = build(chars);
    if (JSON.stringify(digest).length <= budget) return digest;
  }
  return build(0);
}

function groupFilms(cards: ClipCard[]): { item: string; title: string; count: number }[] {
  const by = new Map<string, { item: string; title: string; count: number }>();
  for (const c of cards) {
    const hit = by.get(c.item);
    if (hit) hit.count += 1;
    else by.set(c.item, { item: c.item, title: c.title, count: 1 });
  }
  return [...by.values()].sort((a, b) => b.count - a.count);
}

function postCards(ctx: ReadContext, label: string, cards: ClipCard[]): void {
  ctx.postChat({
    body: '',
    atts: [{ type: 'moments', label, total: cards.length, clips: cards }],
  });
}

function confidenceNote(confidence: string | undefined): string {
  switch (confidence) {
    case 'none':
      return 'CONFIDENCE NONE: the corpus has nothing close to this. The cards are the nearest neighbours, not matches — tell the user, and do not add them to the timeline.';
    case 'weak':
      return 'Confidence weak: loose matches. Read the text of a result before relying on it.';
    default:
      return 'Confidence strong.';
  }
}

const CARDS_NOTE =
  'Every result is already shown to the user as a clip card; do not repeat them with show_clips. `id` is the annotation id: use it as the anchor for similar_shots, steer_shot or a trajectory waypoint.';

// ---------------------------------------------------------------------------
// semantic_search
// ---------------------------------------------------------------------------

export const semanticSearchTool: ReadToolDef = {
  name: 'semantic_search',
  kind: 'read',
  description:
    'Search AI shot descriptions BY MEANING, ranked — finds "aircraft" for "an aeroplane in flight". Covers ONLY the embedded indiancine.ma films (about 243), not pad.ma or transcripts; for exact words, names, other archives or spoken lines use search_moments. ' +
    'Results are shown to the user as clip cards; you get a digest whose `id` values anchor similar_shots, steer_shot and trajectory. ' +
    'kind "scenes" finds longer sequences; kind "objects" finds detected things with a position in the frame (filters min_area/max_area/cx/cy). ' +
    'If the response says confidence "none", nothing matched — say so rather than using the results.',
  schema: {
    type: 'object',
    properties: {
      q: { type: 'string', description: 'describe the shot in plain language' },
      kind: {
        type: 'string',
        enum: ['shots', 'scenes', 'objects'],
        description: 'shots (default), scenes (sequences of shots), objects (things in the frame)',
      },
      limit: { type: 'integer', minimum: 1, maximum: MAX_LIMIT },
      per_film: {
        type: 'integer',
        minimum: 0,
        maximum: 50,
        description: 'max hits from one film; default 3, 0 for no cap',
      },
      filters: ALL_FILTERS,
    },
    required: ['q'],
  },
  describeCall: (a) => {
    const kind = a.kind && a.kind !== 'shots' ? ` (${String(a.kind)})` : '';
    return `Semantic search for ${JSON.stringify(a.q ?? '')}${kind}`;
  },
  resultBudget: 3500,
  async run(args, ctx) {
    const emb = port(ctx);
    if (!emb) return toolError(NOT_ENABLED);
    const q = typeof args.q === 'string' ? args.q.trim() : '';
    if (!q) return toolError('q must describe the shot you want');
    const kind = (args.kind as Kind | undefined) ?? 'shots';
    const limit = clampInt(args.limit, DEFAULT_LIMIT, 1, MAX_LIMIT);
    const perFilm = clampInt(args.per_film, 3, 0, 50);
    const built = toFilters(args.filters, kind);
    if (!built.ok) return toolError(built.error);
    const label = `“${q}” · semantic ${kind} · ${emb.archive}`;

    if (kind === 'objects') {
      let res;
      try {
        res = await emb.searchRegions({
          q,
          mode: 'hybrid',
          limit,
          per_item_limit: perFilm,
          filters: built.filters,
        });
      } catch (err) {
        return failed('semantic_search', err);
      }
      const hits: RegionHit[] = res.results;
      const cards = hits.map((h) => ({
        archive: emb.archive,
        item: h.item.id,
        title: h.item.title || h.item.id,
        in: round(h.t_in),
        out: round(h.t_out),
        note: truncate(`${h.label} — ${h.prose ?? ''}`, CARD_NOTE_CHARS),
      }));
      if (cards.length) postCards(ctx, label, cards);
      return fitDigest(
        (chars) => ({
          ok: true,
          kind,
          count: hits.length,
          shown_to_user: cards.length,
          films: groupFilms(cards),
          sample: hits.slice(0, SAMPLE_ROWS).map((h) => ({
            id: h.annotation_id,
            item: h.item.id,
            in: round(h.t_in),
            out: round(h.t_out),
            title: h.item.title,
            label: h.label,
            box: { cx: round2(h.box.cx), cy: round2(h.box.cy), area: round2(h.box.area) },
            ...(chars > 0 ? { text: truncate(h.prose ?? '', chars) } : {}),
          })),
          note: hits.length
            ? `${CARDS_NOTE} box.cx/cy are the object's centre (0–1 from left/top), area its share of the frame.`
            : 'no objects matched — try other words, drop a filter, or search kind "shots" instead',
        }),
        semanticSearchTool.resultBudget!,
      );
    }

    const body: ShotSearchBody = {
      q,
      mode: 'hybrid',
      limit,
      per_item_limit: perFilm,
      merge_adjacent: true,
      diversify: true,
      rerank: false,
      filters: built.filters,
    };

    if (kind === 'scenes') {
      let res;
      try {
        res = await emb.searchScenes(body);
      } catch (err) {
        return failed('semantic_search', err);
      }
      const hits: SceneHit[] = res.results;
      const cards = hits.map((h) => ({
        archive: emb.archive,
        item: h.item.id,
        title: h.item.title || h.item.id,
        in: round(h.t_in),
        out: round(h.t_out),
        note: truncate(h.text, CARD_NOTE_CHARS),
      }));
      if (cards.length) postCards(ctx, label, cards);
      return fitDigest(
        (chars) => ({
          ok: true,
          kind,
          count: hits.length,
          shown_to_user: cards.length,
          films: groupFilms(cards),
          sample: hits.slice(0, SAMPLE_ROWS).map((h) => ({
            id: h.anchor_id ?? null,
            item: h.item.id,
            in: round(h.t_in),
            out: round(h.t_out),
            title: h.item.title,
            shots: h.shot_count,
            ...(typeof h.score === 'number' ? { score: round2(h.score) } : {}),
            ...(chars > 0 ? { text: truncate(h.text, chars) } : {}),
          })),
          note: hits.length
            ? `${CARDS_NOTE} A scene spans several shots; \`id\` is its first shot.`
            : 'no scenes matched — try other words, drop a filter, or search kind "shots"',
        }),
        semanticSearchTool.resultBudget!,
      );
    }

    let res;
    try {
      res = await emb.searchShots(body);
    } catch (err) {
      return failed('semantic_search', err);
    }
    const hits = res.results;
    const cards = hits.map((h) => shotCard(h, emb.archive));
    if (cards.length) postCards(ctx, label, cards);
    return fitDigest(
      (chars) => ({
        ok: true,
        kind,
        confidence: res.confidence,
        ...(typeof res.top_score === 'number' ? { top_score: round2(res.top_score) } : {}),
        count: hits.length,
        shown_to_user: cards.length,
        films: groupFilms(cards),
        sample: hits.slice(0, SAMPLE_ROWS).map((h) => shotRow(h, chars)),
        note: hits.length
          ? `${confidenceNote(res.confidence)} ${CARDS_NOTE}`
          : 'nothing came back — loosen the filters or try other words',
      }),
      semanticSearchTool.resultBudget!,
    );
  },
};

// ---------------------------------------------------------------------------
// similar_shots
// ---------------------------------------------------------------------------

export const similarShotsTool: ReadToolDef = {
  name: 'similar_shots',
  kind: 'read',
  description:
    'Shots that LOOK LIKE an anchor shot, anywhere in the embedded indiancine.ma films. Anchor by an annotation id from an earlier semantic result, or by {item, at} — a timeline or bin clip\'s item and a concat time inside it. ' +
    'exclude_same_item true means "this image elsewhere". Chain it — anchor on one of the results — to walk a drift montage.',
  schema: {
    type: 'object',
    properties: {
      anchor: ANCHOR,
      limit: { type: 'integer', minimum: 1, maximum: 100 },
      exclude_same_item: { type: 'boolean', description: 'leave out the anchor\'s own film' },
      filters: SHOT_FILTERS,
    },
    required: ['anchor'],
  },
  describeCall: (a) => {
    const parsed = parseAnchor(a.anchor);
    return `Find shots like ${parsed.ok ? anchorLabel(parsed.anchor) : 'an anchor'}`;
  },
  resultBudget: 3500,
  async run(args, ctx) {
    const emb = port(ctx);
    if (!emb) return toolError(NOT_ENABLED);
    const parsed = parseAnchor(args.anchor);
    if (!parsed.ok) return toolError(parsed.error);
    const built = toFilters(args.filters, 'shots');
    if (!built.ok) return toolError(built.error);
    let res;
    try {
      res = await emb.similarShots({
        anchor: parsed.anchor,
        limit: clampInt(args.limit, 20, 1, 100),
        exclude_same_item: args.exclude_same_item === true,
        per_item_limit: 0,
        filters: built.filters,
      });
    } catch (err) {
      return failed('similar_shots', err);
    }
    const cards = res.results.map((h) => shotCard(h, emb.archive));
    if (cards.length) postCards(ctx, `like ${res.anchor.id} · semantic · ${emb.archive}`, cards);
    return fitDigest(
      (chars) => ({
        ok: true,
        anchor: shotRow(res.anchor, chars),
        count: res.results.length,
        shown_to_user: cards.length,
        results: res.results.slice(0, NEIGHBOUR_ROWS).map((h) => shotRow(h, chars)),
        note: cards.length
          ? `${CARDS_NOTE} Results are in order of similarity.`
          : 'nothing passed the filters — loosen them, or drop exclude_same_item',
      }),
      similarShotsTool.resultBudget!,
    );
  },
};

// ---------------------------------------------------------------------------
// steer_shot
// ---------------------------------------------------------------------------

export const steerShotTool: ReadToolDef = {
  name: 'steer_shot',
  kind: 'read',
  description:
    'This shot, but more like a phrase: the shots nearest to the anchor once it is nudged towards `towards` (and away from `away`). "This shot, at night"; "this, but with a crowd"; "calmer". Same anchor forms as similar_shots.',
  schema: {
    type: 'object',
    properties: {
      anchor: ANCHOR,
      towards: { type: 'string', description: 'the quality to add, e.g. "night", "a crowd", "joy"' },
      away: { type: 'string', description: 'optional: the quality to remove, e.g. "daylight"' },
      alpha: {
        type: 'number',
        minimum: 0,
        maximum: 3,
        description: 'how far to nudge; 0.5 gentle (default), 1.5 strong',
      },
      k: { type: 'integer', minimum: 1, maximum: 100 },
      exclude_same_item: { type: 'boolean' },
      filters: SHOT_FILTERS,
    },
    required: ['anchor', 'towards'],
  },
  describeCall: (a) => {
    const parsed = parseAnchor(a.anchor);
    return `Steer ${parsed.ok ? anchorLabel(parsed.anchor) : 'a shot'} towards ${JSON.stringify(a.towards ?? '')}`;
  },
  resultBudget: 3500,
  async run(args, ctx) {
    const emb = port(ctx);
    if (!emb) return toolError(NOT_ENABLED);
    const parsed = parseAnchor(args.anchor);
    if (!parsed.ok) return toolError(parsed.error);
    const towards = typeof args.towards === 'string' ? args.towards.trim() : '';
    if (!towards) return toolError('towards must be a phrase, e.g. "night"');
    const away = typeof args.away === 'string' && args.away.trim() ? args.away.trim() : undefined;
    const built = toFilters(args.filters, 'shots');
    if (!built.ok) return toolError(built.error);
    const alpha =
      typeof args.alpha === 'number' && Number.isFinite(args.alpha)
        ? Math.min(Math.max(args.alpha, 0), 3)
        : 0.5;
    let res;
    try {
      res = await emb.steer({
        anchor: parsed.anchor,
        towards,
        ...(away ? { away } : {}),
        alpha,
        k: clampInt(args.k, 12, 1, 100),
        exclude_same_item: args.exclude_same_item === true,
        filters: built.filters,
      });
    } catch (err) {
      return failed('steer_shot', err);
    }
    const cards = res.results.map((h) => shotCard(h, emb.archive));
    const direction = away ? `“${towards}” not “${away}”` : `“${towards}”`;
    if (cards.length) postCards(ctx, `${res.anchor.id} → ${direction} · semantic · ${emb.archive}`, cards);
    return fitDigest(
      (chars) => ({
        ok: true,
        anchor: shotRow(res.anchor, chars),
        towards,
        ...(away ? { away } : {}),
        alpha,
        count: res.results.length,
        shown_to_user: cards.length,
        results: res.results.slice(0, NEIGHBOUR_ROWS).map((h) => shotRow(h, chars)),
        note: cards.length
          ? `${CARDS_NOTE} A larger alpha moves further from the anchor.`
          : 'nothing passed the filters — loosen them, or lower alpha',
      }),
      steerShotTool.resultBudget!,
    );
  },
};

// ---------------------------------------------------------------------------
// trajectory
// ---------------------------------------------------------------------------

/** "OJV/ABC" — one slash, no spaces — is a shot id; anything else is a phrase. */
const ID_RE = /^[A-Za-z0-9_-]+\/[^\s/]+$/;

function waypointLabel(w: unknown): string {
  if (typeof w === 'string') return w;
  if (w && typeof w === 'object') {
    const o = w as { q?: unknown; id?: unknown };
    if (typeof o.q === 'string') return o.q;
    if (typeof o.id === 'string') return o.id;
  }
  return String(w);
}

/**
 * Normalise the model's waypoints into the service's shape. A bare string is a
 * phrase, or a shot id if it looks like one; an object carries its own filters —
 * the form that lets one arc move from 1950s films into 1960s ones.
 */
function parseWaypoints(
  raw: unknown,
): { ok: true; waypoints: TrajectoryWaypoint[] } | { ok: false; error: string } {
  const list = Array.isArray(raw) ? raw : [];
  if (list.length < 2) return { ok: false, error: 'waypoints needs at least two entries, in order' };
  const out: TrajectoryWaypoint[] = [];
  for (const w of list) {
    if (typeof w === 'string') {
      const text = w.trim();
      if (!text) return { ok: false, error: 'every waypoint must be a phrase or a shot id' };
      out.push(ID_RE.test(text) ? { id: text } : text);
      continue;
    }
    const o = (w ?? {}) as { q?: unknown; id?: unknown; filters?: unknown };
    const q = typeof o.q === 'string' ? o.q.trim() : '';
    const id = typeof o.id === 'string' ? o.id.trim() : '';
    if (!!q === !!id) {
      return { ok: false, error: 'a waypoint object needs exactly one of "q" (a phrase) or "id" (a shot id)' };
    }
    if (id) {
      out.push({ id });
      continue;
    }
    const built = toFilters(o.filters, 'shots');
    if (!built.ok) return built;
    out.push(Object.keys(built.filters).length ? { q, filters: built.filters } : q);
  }
  return { ok: true, waypoints: out };
}

export const trajectoryTool: ReadToolDef = {
  name: 'trajectory',
  kind: 'read',
  description:
    'An ARC of shots: waypoints in order — plain phrases, or annotation ids from earlier results — with real shots along the path in meaning between each pair. Returns the whole chain in order, ready for add_sequence. ' +
    'One call builds the whole arc; do not search each waypoint separately. Use `subject` for what every waypoint is about ("mill workers"), and filters.min_duration/max_duration (e.g. 2–12) so every shot is usable. ' +
    `The chain is at most ${MAX_CHAIN} shots: waypoints + steps_per_leg × (waypoints − 1).`,
  schema: {
    type: 'object',
    properties: {
      waypoints: {
        type: 'array',
        maxItems: 8,
        items: {
          type: 'string',
          anyOf: [
            { type: 'string' },
            {
              type: 'object',
              properties: {
                q: { type: 'string', description: 'a phrase' },
                id: { type: 'string', description: 'or a shot id from an earlier result' },
                filters: SHOT_FILTERS,
              },
            },
          ],
          description:
            'a phrase ("sad"), a shot id ("OJV/ABC"), or an object {"q": "…", "filters": {…}} / {"id": "…"} when this stop needs its own filters',
        },
        description:
          '2–8 in order, e.g. ["sad", "angry", "joyful"], or [{"q": "trumpets", "filters": {"year": {"from": 1950, "to": 1959}}}, {"q": "meditation", "filters": {"year": {"from": 1960, "to": 1969}}}]',
      },
      subject: {
        type: 'string',
        description: 'what every waypoint is about, e.g. "mill workers" — combined with each phrase',
      },
      steps_per_leg: {
        type: 'integer',
        minimum: 0,
        maximum: 6,
        description: 'interior shots between consecutive waypoints; default 3, or fewer if the chain would exceed 20',
      },
      one_per_film: { type: 'boolean', description: 'never two shots from the same film' },
      filters: SHOT_FILTERS,
    },
    required: ['waypoints'],
  },
  describeCall: (a) => {
    const wps = Array.isArray(a.waypoints) ? a.waypoints.map(waypointLabel) : [];
    const subject = a.subject ? ` (${String(a.subject)})` : '';
    return `Trajectory ${wps.join(' → ')}${subject}`;
  },
  resultBudget: 6000,
  async run(args, ctx) {
    const emb = port(ctx);
    if (!emb) return toolError(NOT_ENABLED);
    const parsedWaypoints = parseWaypoints(args.waypoints);
    if (!parsedWaypoints.ok) return toolError(parsedWaypoints.error);
    const raw = parsedWaypoints.waypoints;
    const labels = raw.map(waypointLabel);
    const legs = raw.length - 1;
    // Default to the plan's three steps, or fewer when that would overrun what
    // add_sequence can take in one call.
    const fitsDefault = Math.min(DEFAULT_STEPS, Math.floor((MAX_CHAIN - raw.length) / legs));
    const steps = clampInt(args.steps_per_leg, Math.max(fitsDefault, 0), 0, 6);
    const chainLength = raw.length + steps * legs;
    if (chainLength > MAX_CHAIN) {
      const maxSteps = Math.floor((MAX_CHAIN - raw.length) / legs);
      return toolError(
        `${raw.length} waypoints with steps_per_leg ${steps} would give ${chainLength} shots; add_sequence takes at most ${MAX_CHAIN}. Use steps_per_leg ${Math.max(maxSteps, 0)} or fewer waypoints.`,
      );
    }
    const built = toFilters(args.filters, 'shots');
    if (!built.ok) return toolError(built.error);
    const subject = typeof args.subject === 'string' && args.subject.trim() ? args.subject.trim() : undefined;

    let res;
    try {
      res = await emb.trajectory({
        waypoints: raw,
        ...(subject ? { subject } : {}),
        steps_per_leg: steps,
        k_candidates: 8,
        one_per_film: args.one_per_film === true,
        filters: built.filters,
      });
    } catch (err) {
      return failed('trajectory', err);
    }
    const chain: ChainEntry[] = res.chain;
    const cards = chain.map((h) => shotCard(h, emb.archive));
    const label = `arc: ${labels.join(' → ')}${subject ? ` (${subject})` : ''} · semantic · ${emb.archive}`;
    if (cards.length) postCards(ctx, label, cards);
    return fitDigest(
      (chars) => ({
        ok: true,
        count: chain.length,
        waypoints: res.waypoints.map((w) => ({ input: w.input, kind: w.kind, id: w.id })),
        chain: chain.map((h) => ({ pos: h.pos, role: h.role, ...shotRow(h, chars) })),
        note:
          'The chain is shown to the user as cards, in this order. To assemble it: add_sequence with these clips in this order (archive "' +
          emb.archive +
          '", exact in/out), then propose. `id` values are anchors for similar_shots or steer_shot.',
      }),
      trajectoryTool.resultBudget!,
    );
  },
};

/**
 * The semantic tools as a registry of their own. `state/agent.ts` adds them to
 * the run's tool list only when the composer's toggle is on; with it off, the
 * three `ALL_TOOLS` sites in the codebase are unchanged.
 */
export const EMBEDDING_TOOLS: ReadToolDef[] = [
  semanticSearchTool,
  similarShotsTool,
  steerShotTool,
  trajectoryTool,
];
